import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DxDataGridComponent} from 'devextreme-angular';
import config from 'devextreme/core/config';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';
import repaintFloatingActionButton from 'devextreme/ui/speed_dial_action/repaint_floating_action_button';
import {BehaviorSubject, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {ReportsService} from 'src/app/modules/shared/services/reports.service';
import {FusionAuthManagementService, FusionAuthService} from '../../../../../shared/services/fusion-auth';
import {PWADetectorService, ScreenService} from '../../../../../shared/services/general';
import {FileUtils} from '../../../../../shared/utils';
import {RequisitionService} from '../../../../shared/services/requisition.service';
import {RequisitionSearch, RequisitionStatus} from '../../models/requisition-search';
import {MultipleRequisitionsSignature} from '../../../../shared/models/signature';
import {SignatureRepresentation} from '../signature-dialog/signature-dialog.component';
import {RegistrationRequest} from "../../../../../shared/models/fusion-auth";
import {environment} from "../../../../../../environments/environment";
import {CREATED} from "http-status-codes";
import {alert} from "devextreme/ui/dialog";

@Component({
  selector: 'app-requisition-search-content',
  templateUrl: './requisition-search-content.component.html',
  styleUrls: ['./requisition-search-content.component.scss'],
})
export class RequisitionSearchContentComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DxDataGridComponent, {static: true}) public dxDataGridComponent?: DxDataGridComponent;
  @Input() public filtersSubmitted: EventEmitter<any> = new EventEmitter<any>();
  public storageName = 'requisitionGridStorage';
  public signRequisitionDialogVisible = false;
  public selectedItemsIds: number[] = [];
  public signedRequisitionSelected = false;
  public isLoading = false;
  // @ts-ignore
  public dataSource: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  public emailFaxVisible = false;
  public type: string = '';
  public isPhoneOrTablet: boolean = false;
  public isPhone: boolean = false;
  public printDialogVisible = false;
  public filters: any;
  public user: any;
  public updateBillToVisible = false;
  private onDestroy$ = new Subject();

  constructor(
    private pwaDetectorService: PWADetectorService,
    private requisitionService: RequisitionService,
    private router: Router,
    public route: ActivatedRoute,
    private elRef: ElementRef,
    public fusionAuthService: FusionAuthService,
    private fusionAuthManagement: FusionAuthManagementService,
    private reportsService: ReportsService,
    private renderer: Renderer2,
    private screen: ScreenService,
  ) {
  }

  public get ButtonsDisabled() {
    return this.selectedItemsIds.length === 0;
  }

  public itemsSelected(ids: number[]) {
    this.selectedItemsIds = ids;
    if (this.dxDataGridComponent?.instance) {
      this.signedRequisitionSelected =
        this.dxDataGridComponent.instance.getSelectedRowsData().filter(r => r.hasSignature).length !== 0;
    }
  }

  public async rowClick(e: any) {
    if (e.rowType === 'data' && !this.isPhoneOrTablet) {
      await this.sendToPreview(e.data.id);
    }
  }

  public ngOnInit() {
    this.waitByUser();
  }

  private waitByUser() {
    this.user = this.fusionAuthService.Profile;

    this.responsiveValues();
    this.screen.changed
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.dxDataGridComponent?.instance.clearSelection();
        this.responsiveValues();
      });

    if (this.user) {
      this.filtersSubmitted
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(filters => {
          this.filters = filters;
          setTimeout(() => {
            this.setFilters(this.filters, this.user);
          }, 500);
        });
    } else {
      setTimeout(() => {
        this.waitByUser();
      }, 1000);
    }
  }

  public setFilters(filters: any, user: any) {
    const role = user.registrations[0].roles[0];
    if (typeof filters.startDate === 'number') {
      filters.startDate = new Date(filters.startDate);
    }

    if (typeof filters.endDate === 'number') {
      filters.endDate = new Date(filters.endDate);
    }

    switch (role) {
      case 'Client Admin':
      case 'Client Basic':
        const clientIds = user ? user.data.clients : this.fusionAuthService.Profile.data.clients;
        this.dataSource.next(new DataSource({
          store: this.requisitionService.requisitionStore({
            externalFilters: filters,
            clientIds,
            dataSource: this.dataSource,
          }),
        }));
        break;
      case 'Patient':
        const patientIds = user ? user.data.patients : this.fusionAuthService.Profile.data.patients;
        this.dataSource.next(new DataSource({
          store: this.requisitionService.requisitionStore({
            externalFilters: filters,
            patientIds,
            dataSource: this.dataSource,
          }),
        }));
        break;
      case 'Physician':
        const physicianIds = user ? user.data.physicians : this.fusionAuthService.Profile.data.physicians;
        this.dataSource.next(new DataSource({
          store: this.requisitionService.requisitionStore({
            externalFilters: filters,
            physicianIds,
            dataSource: this.dataSource,
          }),
        }));
        break;
      default:
        this.dataSource.next(new DataSource({
          store: this.requisitionService.requisitionStore({
            externalFilters: filters,
            dataSource: this.dataSource,
          }),
        }));
    }
  }

  private responsiveValues() {
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.isPhone = this.screen.sizes['screen-x-small'];

    const offset = this.isPhone ? '-15 75' : '-25 75';
    config({
      floatingActionButtonConfig: {
        icon: 'rowfield',
        shading: true,
        direction: 'down',
        maxSpeedDialActionCount: 6,
        position: {
          of: 'body',
          my: 'right top',
          at: 'right top',
          offset,
        },
      },
    });
    repaintFloatingActionButton();
  }

  public getStatus(requisition: RequisitionSearch) {
    let color;
    let status;
    switch (requisition.status) {
      case RequisitionStatus.Canceled:
        color = 'ocl-status-black';
        status = 'CANCELED';
        break;
      case RequisitionStatus.Done:
        color = 'ocl-status-green';
        status = 'DONE';
        break;
      case RequisitionStatus.Pending:
        color = 'ocl-status-yellow';
        status = 'PENDING';
        break;
      default:
        color = 'ocl-status-black';
        status = 'NONE';
        break;
    }

    return `<div style="width: 100px;" class="${color}">${status}</div>`;
  }

  public ngAfterViewInit(): void {
    const storageString = localStorage.getItem(this.storageName);
    const storedConfig = JSON.parse(storageString as string);
    if (storedConfig) {
      storedConfig.selectedRowKeys = [];
      localStorage.setItem(this.storageName, JSON.stringify(storedConfig));
    }
  }

  public get FABDisabled() {
    return this.isPhoneOrTablet && this.selectedItemsIds ? this.selectedItemsIds.length > 0 : false;
  }

  public signRequisitions() {
    this.signRequisitionDialogVisible = true;
  }

  public signDialogClosed(signatureDialogResponse: SignatureRepresentation) {
    if (signatureDialogResponse) {
      const signature = new MultipleRequisitionsSignature();
      signature.image = signatureDialogResponse.image;
      signature.username = signatureDialogResponse.username;
      signature.requisitionsIds = this.selectedItemsIds;

      this.dxDataGridComponent?.instance.beginCustomLoading(
        this.selectedItemsIds.length === 1 ? 'Signing requisition' : 'Signing requisitions',
      );
      const user = this.fusionAuthService.Profile;
      user.data = {
        signImage: signatureDialogResponse.image,
        clients: user.data !== undefined ? user.data.clients : [],
        patients: user.data !== undefined ? user.data.patients : [],
        physicians: user.data !== undefined ? user.data.physicians : [],
      };
      const request: RegistrationRequest = {
        user,
        registration: {
          applicationId: environment.fusionAuth.applicationId,
          roles: [user.role],
          username: user.username,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      this.fusionAuthManagement
        .updateUser(user.id, request)
        .pipe(take(1))
        .subscribe(response => {
          if (response.status === CREATED) {
            this.requisitionService.signRequisitions(signature)
              .pipe(take(1))
              .subscribe(() => {
                this.fusionAuthService.SignatureImage = signatureDialogResponse.image;
                this.dxDataGridComponent?.instance.endCustomLoading();
                notify(this.selectedItemsIds.length === 1 ? 'Requisition Signed!' : 'Requisitions Signed!', 'success');
                this.dxDataGridComponent?.instance.refresh();
              });
          }
        });

      /*this.requisitionService.signRequisitions(signature)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.dxDataGridComponent?.instance.endCustomLoading();
          notify(this.selectedItemsIds.length === 1 ? 'Requisition Signed!' : 'Requisitions Signed!', 'success');
          this.dxDataGridComponent?.instance.refresh();
        });*/
    }

    this.signRequisitionDialogVisible = false;
  }

  public async sendToPreview(id: number) {
    if (this.pwaDetectorService.isPWA()) {
      await this.router.navigate(['/secured/requisitions/create', {id}]);
    } else {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/secured/requisitions/create', {id}]),
      );
      window.open(url, '_blank');
    }
  }

  public printRequisitions() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateRequisitionReport(this.selectedItemsIds)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public async printABN() {
    await alert("Please, note that requisitions with no ABN will not be included in the report.", "ABN Warning");
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');

    const ids = this.dxDataGridComponent?.instance.getSelectedRowsData().filter(r => r.hasAdvancedBeneficiaryNotice).map(x => x.id);
    if (ids && ids.length > 0) {
      this.reportsService.generateABNReport(ids, 'requisition')
        .pipe(take(1))
        .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
    } else {
      this.dxDataGridComponent?.instance.endCustomLoading();
      await alert("No requisitions with ABN selected.", "ABN Warning");
    }
  }

  public printRequisitionLog() {
    this.dxDataGridComponent?.instance.beginCustomLoading('Generating Report');
    this.reportsService.generateRequisitionLog(this.selectedItemsIds)
      .pipe(take(1))
      .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this, true));
  }

  public openEmailFax(type: string) {
    this.type = type;
    this.emailFaxVisible = true;
  }

  public openUpdateBillTo() {
    this.updateBillToVisible = true;
  }

  public closeUpdateBillTo() {
    this.updateBillToVisible = false;
    this.setFilters(this.filters, this.user);
  }

  public printLabels() {
    this.printDialogVisible = true;
  }

  public onContentReady(): void {
    this.dxDataGridComponent?.instance.clearSelection();
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
